import React, { useState, useEffect, useRef, useCallback } from 'react';
import Chart from 'react-apexcharts';
import { useAuth } from '../../context/AuthContext';
import {
  MessageCircle, X, Send, Loader2, Bot,
  Sparkles, AlertCircle, Settings, RefreshCw,
  Activity, ShieldAlert, BarChart3, Maximize2, Minimize2
} from 'lucide-react';
import { sendAiMessage, getAiPresets, getAiConfig } from '../../api/aiChat.api';
import { UserRole } from '../../constants/userRole';

const TONE_STYLES = {
  positive: 'border-emerald-200/80 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200',
  warning: 'border-amber-200/80 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200',
  neutral: 'border-slate-200/80 bg-slate-50 text-slate-700 dark:border-slate-600/40 dark:bg-slate-700/40 dark:text-slate-200',
  critical: 'border-rose-200/80 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200',
};

const ALERT_STYLES = {
  info: 'border-sky-200/80 bg-sky-50 text-sky-700 dark:border-sky-500/20 dark:bg-sky-500/10 dark:text-sky-200',
  warning: 'border-amber-200/80 bg-amber-50 text-amber-700 dark:border-amber-500/20 dark:bg-amber-500/10 dark:text-amber-200',
  critical: 'border-rose-200/80 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200',
  positive: 'border-emerald-200/80 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10 dark:text-emerald-200',
};

const formatChartValue = (value, suffix = '') => {
  const num = Number(value);
  if (!Number.isFinite(num)) return `${value ?? ''}${suffix}`;
  return `${Number.isInteger(num) ? num.toFixed(0) : num.toFixed(1)}${suffix}`;
};

const buildChartConfig = (chart, isDark) => {
  const chartType = ['bar', 'line', 'area'].includes(chart?.chartType) ? chart.chartType : 'bar';
  return {
    type: chartType,
    series: (chart?.series || []).map((item) => ({
      name: item.name,
      data: item.data || [],
    })),
    options: {
      chart: {
        type: chartType,
        toolbar: { show: false },
        background: 'transparent',
        sparkline: { enabled: false },
      },
      theme: { mode: isDark ? 'dark' : 'light' },
      colors: chart?.colors?.length ? chart.colors : ['#0f766e', '#f97316'],
      dataLabels: { enabled: false },
      stroke: {
        curve: chartType === 'bar' ? 'straight' : 'smooth',
        width: chartType === 'bar' ? 0 : 3,
      },
      fill: chartType === 'area'
        ? {
            type: 'gradient',
            gradient: {
              shadeIntensity: 0.5,
              opacityFrom: 0.35,
              opacityTo: 0.04,
              stops: [0, 95, 100],
            },
          }
        : { opacity: 0.95 },
      grid: {
        borderColor: isDark ? 'rgba(148, 163, 184, 0.12)' : 'rgba(148, 163, 184, 0.18)',
        strokeDashArray: 4,
      },
      legend: {
        show: (chart?.series || []).length > 1,
        labels: { colors: isDark ? '#cbd5e1' : '#475569' },
      },
      xaxis: {
        categories: chart?.categories || [],
        labels: {
          style: { colors: isDark ? '#94a3b8' : '#64748b', fontSize: '11px' },
          rotate: -20,
        },
        axisBorder: { show: false },
        axisTicks: { show: false },
      },
      yaxis: {
        title: {
          text: chart?.yAxisTitle || undefined,
          style: { color: isDark ? '#94a3b8' : '#64748b', fontSize: '11px', fontWeight: 500 },
        },
        labels: {
          style: { colors: isDark ? '#94a3b8' : '#64748b', fontSize: '11px' },
          formatter: (value) => formatChartValue(value, chart?.valueSuffix || ''),
        },
      },
      tooltip: {
        theme: isDark ? 'dark' : 'light',
        y: {
          formatter: (value) => formatChartValue(value, chart?.valueSuffix || ''),
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 8,
          columnWidth: '48%',
        },
      },
    },
  };
};

/* ═══════════════════════════════════════════════════════════ */
/*  AI Chat Panel — Floating chat widget                       */
/* ═══════════════════════════════════════════════════════════ */
const AiChatPanel = () => {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [presets, setPresets] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [showPresets, setShowPresets] = useState(true);
  const [showConfig, setShowConfig] = useState(false);
  const [configStatus, setConfigStatus] = useState(null);
  const scrollRef = useRef(null);
  const isDark = document.documentElement.classList.contains('dark');

  const isAdmin = user?.role === UserRole.Admin;

  // Load presets
  useEffect(() => {
    if (open && presets.length === 0) {
      (async () => {
        try {
          const res = await getAiPresets();
          setPresets(res.data?.data || []);
        } catch { }
      })();
    }
  }, [open, presets.length]);

  // Auto scroll
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, loading]);

  // Load config status for admin (no secrets returned)
  useEffect(() => {
    if (showConfig && isAdmin) {
      (async () => {
        try {
          const res = await getAiConfig();
          const d = res.data?.data;
          setConfigStatus(d);
        } catch { }
      })();
    }
  }, [showConfig, isAdmin]);

  useEffect(() => {
    if (!open) {
      setIsExpanded(false);
    }
  }, [open]);

  useEffect(() => {
    if (!open || !isExpanded) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open, isExpanded]);

  useEffect(() => {
    if (!open || !isExpanded) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setIsExpanded(false);
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [open, isExpanded]);

  const handleSend = useCallback(async (text) => {
    const msg = text || input.trim();
    if (!msg) return;
    setInput('');
    setShowPresets(false);
    setMessages(prev => [...prev, { role: 'user', content: msg }]);
    setLoading(true);

    try {
      const res = await sendAiMessage({ message: msg, conversationId });
      const data = res.data?.data;
      if (data) {
        setConversationId(data.conversationId);
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.response,
          insights: data.insights || [],
          alerts: data.alerts || [],
          charts: data.charts || [],
          metadata: data.metadata || null,
        }]);
      } else {
        setMessages(prev => [...prev, { role: 'error', content: res.data?.message || 'Failed to get response' }]);
      }
    } catch (err) {
      const errMsg = err.response?.data?.message || err.message || 'Network error';
      setMessages(prev => [...prev, { role: 'error', content: errMsg }]);
    }
    setLoading(false);
  }, [input, conversationId]);

  const handleNewChat = () => {
    setMessages([]);
    setConversationId(null);
    setShowPresets(true);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  /* ─── Renders ─── */
  const renderInsightCard = (card, index) => {
    const tone = TONE_STYLES[card.tone] || TONE_STYLES.neutral;
    return (
      <div key={`${card.id || card.title}-${index}`} className={`rounded-2xl border px-3.5 py-3 ${tone}`}>
        <p className="text-[10px] font-semibold uppercase tracking-[0.16em] opacity-70">{card.title}</p>
        <p className="mt-2 text-xl font-semibold leading-none">{card.value}</p>
        <p className="mt-2 text-xs leading-relaxed opacity-80">{card.subtitle}</p>
      </div>
    );
  };

  const renderAlert = (alert, index) => {
    const tone = ALERT_STYLES[alert.severity] || ALERT_STYLES.info;
    return (
      <div key={`${alert.title}-${index}`} className={`rounded-2xl border px-3.5 py-3 ${tone}`}>
        <div className="flex items-start gap-2">
          <ShieldAlert className="mt-0.5 h-4 w-4 flex-shrink-0" />
          <div>
            <div className="flex items-center gap-2">
              <p className="text-sm font-semibold">{alert.title}</p>
              <span className="rounded-full bg-black/5 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.14em] dark:bg-white/10">
                {alert.count}
              </span>
            </div>
            <p className="mt-1 text-xs leading-relaxed opacity-80">{alert.description}</p>
          </div>
        </div>
      </div>
    );
  };

  const renderChartCard = (chart, index) => {
    const config = buildChartConfig(chart, isDark);
    const chartHeight = isExpanded ? Math.max(chart.height || 250, 340) : (chart.height || 250);
    if (!config.series.length || !chart?.categories?.length) {
      return null;
    }

    return (
      <div key={`${chart.id || chart.title}-${index}`} className="rounded-[24px] border border-slate-200/80 bg-white/85 p-3.5 shadow-sm backdrop-blur dark:border-slate-700/60 dark:bg-slate-900/60">
        <div className="mb-3 flex items-start justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{chart.title}</p>
            {chart.subtitle ? (
              <p className="mt-1 text-xs leading-relaxed text-slate-500 dark:text-slate-400">{chart.subtitle}</p>
            ) : null}
          </div>
          <div className="rounded-2xl border border-teal-200/70 bg-teal-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-teal-700 dark:border-teal-500/20 dark:bg-teal-500/10 dark:text-teal-200">
            {chart.chartType || 'bar'}
          </div>
        </div>
        <Chart
          options={config.options}
          series={config.series}
          type={config.type}
          height={chartHeight}
        />
        {chart.details?.length ? (
          <div className={`mt-4 grid gap-2 ${isExpanded ? 'md:grid-cols-2 xl:grid-cols-3' : 'sm:grid-cols-2'}`}>
            {chart.details.map((detail, detailIndex) => (
              <div
                key={`${detail.label || 'detail'}-${detailIndex}`}
                className="rounded-2xl border border-slate-200/80 bg-slate-50/90 p-3 dark:border-slate-700/60 dark:bg-slate-950/40"
              >
                <p className="text-xs font-semibold text-slate-800 dark:text-slate-100">{detail.label}</p>
                {detail.subtitle ? (
                  <p className="mt-1 text-[11px] leading-relaxed text-slate-500 dark:text-slate-400">{detail.subtitle}</p>
                ) : null}
                {detail.metrics?.length ? (
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {detail.metrics.map((metric, metricIndex) => (
                      <span
                        key={`${detail.label}-${metric.label}-${metricIndex}`}
                        className="rounded-full border border-slate-200/80 bg-white px-2.5 py-1 text-[11px] font-medium text-slate-600 dark:border-slate-700/60 dark:bg-slate-900/80 dark:text-slate-300"
                      >
                        <span className="font-semibold text-slate-800 dark:text-slate-100">{metric.label}:</span>{' '}
                        {metric.value}
                      </span>
                    ))}
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}
      </div>
    );
  };

  const renderAssistantMessage = (msg, i) => {
    const scope = msg.metadata?.scope;
    return (
      <div key={i} className="mb-4 flex gap-3">
        <div className="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-500 via-cyan-500 to-sky-500 shadow-lg shadow-cyan-500/25">
          <Bot className="h-4.5 w-4.5 text-white" />
        </div>
        <div className={`${isExpanded ? 'max-w-full' : 'max-w-[92%]'} flex-1 space-y-3`}>
          <div className="rounded-[24px] rounded-bl-md border border-slate-200/80 bg-white/90 px-4 py-3.5 text-sm leading-relaxed text-slate-800 shadow-sm backdrop-blur dark:border-slate-700/60 dark:bg-slate-900/70 dark:text-slate-100">
            {scope ? (
              <div className="mb-3 flex flex-wrap gap-2">
                <span className="rounded-full border border-cyan-200/80 bg-cyan-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-cyan-700 dark:border-cyan-500/20 dark:bg-cyan-500/10 dark:text-cyan-200">
                  {scope.viewerRole} scope
                </span>
                <span className="rounded-full border border-slate-200/80 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-600 dark:border-slate-600/50 dark:bg-slate-700/40 dark:text-slate-200">
                  {scope.visibleUsers} people
                </span>
                {scope.visibleTeams ? (
                  <span className="rounded-full border border-slate-200/80 bg-slate-50 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-600 dark:border-slate-600/50 dark:bg-slate-700/40 dark:text-slate-200">
                    {scope.visibleTeams} teams
                  </span>
                ) : null}
              </div>
            ) : null}
            <div className="whitespace-pre-wrap">{msg.content}</div>
            {scope?.scopeLabel ? (
              <p className="mt-3 border-t border-slate-200/80 pt-3 text-xs leading-relaxed text-slate-500 dark:border-slate-700/70 dark:text-slate-400">
                {scope.scopeLabel}
              </p>
            ) : null}
          </div>

          {msg.insights?.length ? (
            <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
              {msg.insights.map(renderInsightCard)}
            </div>
          ) : null}

          {msg.alerts?.length ? (
            <div className="space-y-2">
              {msg.alerts.map(renderAlert)}
            </div>
          ) : null}

          {msg.charts?.length ? (
            <div className="space-y-3">
              {msg.charts.map(renderChartCard)}
            </div>
          ) : null}

          {msg.content?.trim() &&
          !(msg.insights?.length || msg.alerts?.length || msg.charts?.length) ? (
            <p className="text-[11px] leading-relaxed text-slate-400 dark:text-slate-500 px-0.5">
              No separate charts or insight cards for this reply—the narrative above has the full answer.
            </p>
          ) : null}
        </div>
      </div>
    );
  };

  const renderMessage = (msg, i) => {
    if (msg.role === 'user') {
      return (
        <div key={i} className="flex justify-end mb-3">
          <div className="max-w-[85%] rounded-2xl rounded-br-md bg-gradient-to-r from-cyan-600 to-teal-500 px-4 py-2.5 text-sm leading-relaxed text-white shadow-lg shadow-cyan-500/20">
            {msg.content}
          </div>
        </div>
      );
    }
    if (msg.role === 'error') {
      return (
        <div key={i} className="flex gap-2 mb-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div className="max-w-[85%] bg-red-50 dark:bg-red-500/10 text-red-600 dark:text-red-400 px-4 py-2.5 rounded-2xl rounded-bl-md text-sm">
            {msg.content}
          </div>
        </div>
      );
    }
    return renderAssistantMessage(msg, i);
  };

  const panelWrapperClass = isExpanded
    ? 'fixed inset-3 sm:inset-6 lg:inset-10 z-[120] transition-all duration-300'
    : 'fixed bottom-24 right-6 z-[120] w-[420px] max-w-[calc(100vw-48px)] transition-all duration-300';

  const panelClass = isExpanded
    ? 'flex h-full flex-col overflow-hidden rounded-[36px] border border-slate-200/80 bg-white/95 shadow-2xl backdrop-blur dark:border-slate-700/60 dark:bg-slate-900/90'
    : 'flex h-[620px] flex-col overflow-hidden rounded-[32px] border border-slate-200/80 bg-white/95 shadow-2xl backdrop-blur dark:border-slate-700/60 dark:bg-slate-900/90';

  return (
    <>
      {/* Floating toggle button */}
      <button
        onClick={() => setOpen(!open)}
        className={`fixed bottom-6 right-6 z-[100] w-14 h-14 rounded-full shadow-xl flex items-center justify-center transition-all duration-300
          ${open
            ? 'bg-gray-600 dark:bg-gray-700 hover:bg-gray-700 dark:hover:bg-gray-600 scale-90'
            : 'bg-gradient-to-r from-cyan-600 via-teal-500 to-emerald-400 hover:from-cyan-500 hover:to-emerald-300 scale-100 hover:scale-110'
          }`}
      >
        {open ? <X className="w-6 h-6 text-white" /> : <MessageCircle className="w-6 h-6 text-white" />}
        {!open && (
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white dark:border-gray-900 animate-pulse" />
        )}
      </button>

      {open && isExpanded ? (
        <div className="fixed inset-0 z-[90] bg-slate-950/30 backdrop-blur-sm" />
      ) : null}

      {/* Chat panel */}
      <div className={`${panelWrapperClass} ${open ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 translate-y-4 pointer-events-none'}`}>
        <div className={panelClass}>
          {/* Header */}
          <div className="flex items-center justify-between bg-gradient-to-r from-cyan-700 via-teal-600 to-emerald-500 px-5 py-4 text-white">
            <div className="flex items-center gap-2.5">
              <div className="flex h-9 w-9 items-center justify-center rounded-2xl bg-white/15 backdrop-blur">
                <Sparkles className="w-4 h-4" />
              </div>
              <div>
                <p className="text-sm font-bold">Operis AI Assistant</p>
                <p className="text-[10px] text-white/75">Charts, alerts, and scoped team analysis</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={handleNewChat} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="New conversation">
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                onClick={() => setIsExpanded((prev) => !prev)}
                className="p-1.5 rounded-lg hover:bg-white/10 transition-colors"
                title={isExpanded ? 'Reduce chat size' : 'Expand chat'}
              >
                {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
              </button>
              {isAdmin && (
                <button onClick={() => setShowConfig(!showConfig)} className="p-1.5 rounded-lg hover:bg-white/10 transition-colors" title="AI Settings">
                  <Settings className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>

          {/* Config panel (admin) — read-only status */}
          {showConfig && isAdmin && (
            <div className="p-4 bg-gray-50 dark:bg-gray-700/30 border-b border-gray-200 dark:border-gray-600/50">
              <h4 className="text-xs font-bold text-gray-600 dark:text-gray-300 mb-2 uppercase">Azure AI Configuration</h4>
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-medium ${configStatus?.isConfigured ? 'text-green-500' : 'text-amber-500'}`}>
                    {configStatus?.isConfigured ? '✓ Configured' : '○ Not configured'}
                  </span>
                </div>
                {configStatus?.deploymentName && (
                  <p className="text-xs text-gray-500 dark:text-gray-400">Current Model: {configStatus.deploymentName}</p>
                )}
                
              </div>
            </div>
          )}

          {/* Messages area */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto bg-[radial-gradient(circle_at_top,_rgba(13,148,136,0.07),_transparent_45%),linear-gradient(180deg,rgba(248,250,252,0.96),rgba(255,255,255,0.82))] p-4 dark:bg-[radial-gradient(circle_at_top,_rgba(13,148,136,0.18),_transparent_38%),linear-gradient(180deg,rgba(15,23,42,0.92),rgba(2,6,23,0.96))]" style={{ scrollbarWidth: 'thin' }}>
            {/* Welcome */}
            {messages.length === 0 && (
              <div className="text-center py-6">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-[22px] bg-gradient-to-br from-cyan-100 via-teal-50 to-emerald-100 shadow-sm dark:from-cyan-500/20 dark:via-teal-500/10 dark:to-emerald-500/20">
                  <Bot className="h-8 w-8 text-cyan-600 dark:text-cyan-300" />
                </div>
                <h3 className="text-sm font-bold text-slate-800 dark:text-slate-100">Hi, {user?.firstName || 'there'}!</h3>
                <p className="mx-auto mt-2 max-w-[300px] text-xs leading-relaxed text-slate-500 dark:text-slate-400">
                  Ask for team performance, overdue work, billable mix, project codes, production throughput, or historical trends. If you want a chart, say so explicitly.
                </p>
                <div className="mt-5 grid grid-cols-3 gap-2 text-left">
                  <div className="rounded-2xl border border-cyan-200/70 bg-white/80 p-3 shadow-sm dark:border-cyan-500/10 dark:bg-slate-900/40">
                    <Activity className="h-4 w-4 text-cyan-600 dark:text-cyan-300" />
                    <p className="mt-2 text-[11px] font-semibold text-slate-700 dark:text-slate-200">Performance</p>
                  </div>
                  <div className="rounded-2xl border border-amber-200/70 bg-white/80 p-3 shadow-sm dark:border-amber-500/10 dark:bg-slate-900/40">
                    <ShieldAlert className="h-4 w-4 text-amber-600 dark:text-amber-300" />
                    <p className="mt-2 text-[11px] font-semibold text-slate-700 dark:text-slate-200">Alerts</p>
                  </div>
                  <div className="rounded-2xl border border-emerald-200/70 bg-white/80 p-3 shadow-sm dark:border-emerald-500/10 dark:bg-slate-900/40">
                    <BarChart3 className="h-4 w-4 text-emerald-600 dark:text-emerald-300" />
                    <p className="mt-2 text-[11px] font-semibold text-slate-700 dark:text-slate-200">Charts</p>
                  </div>
                </div>
              </div>
            )}

            {/* Messages */}
            {messages.map(renderMessage)}

            {/* Loading */}
            {loading && (
              <div className="flex gap-2 mb-3">
                <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-cyan-600 to-teal-500">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="flex items-center gap-2 rounded-2xl rounded-bl-md bg-white/85 px-4 py-3 shadow-sm dark:bg-slate-800/75">
                  <Loader2 className="h-4 w-4 animate-spin text-cyan-600 dark:text-cyan-300" />
                  <span className="text-xs text-slate-500 dark:text-slate-400">Compiling scoped analysis…</span>
                </div>
              </div>
            )}

            {/* Presets */}
            {showPresets && messages.length === 0 && presets.length > 0 && (
              <div className="mt-3">
                <p className="text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase mb-2 px-1">Quick Questions</p>
                <div className="space-y-1.5">
                  {presets.slice(0, 6).map((q) => (
                    <button
                      key={q.id}
                      onClick={() => handleSend(q.question)}
                      className="w-full rounded-2xl border border-transparent bg-white/80 px-3 py-2.5 text-left text-xs text-slate-600 shadow-sm transition-colors hover:border-cyan-200 hover:bg-cyan-50 hover:text-cyan-700 dark:bg-slate-800/50 dark:text-slate-300 dark:hover:border-cyan-500/20 dark:hover:bg-cyan-500/10 dark:hover:text-cyan-200"
                    >
                      {q.question}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-slate-200/80 px-4 pb-4 pt-3 dark:border-slate-700/60">
            <div className="flex items-center gap-2 rounded-[22px] border border-slate-200 bg-white px-3 py-1.5 shadow-sm transition-colors focus-within:border-cyan-300 dark:border-slate-700 dark:bg-slate-800/80 dark:focus-within:border-cyan-500/30">
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Ask for team analysis, overdue risks, or charts…"
                disabled={loading}
                className="flex-1 bg-transparent py-1.5 text-sm text-slate-700 outline-none placeholder:text-slate-400 dark:text-slate-200 dark:placeholder:text-slate-500"
              />
              <button
                onClick={() => handleSend()}
                disabled={loading || !input.trim()}
                className="flex-shrink-0 rounded-2xl bg-gradient-to-r from-cyan-600 to-teal-500 p-2 text-white transition-colors hover:from-cyan-500 hover:to-teal-400 disabled:opacity-40"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AiChatPanel;
